import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_ORGANIZATION_LAND_TYPE_PATH,
  GetOrganizationLandTypeErrorDto,
  GetOrganizationLandTypeSuccessDto,
} from '@/api/rest/resources/types/organization';
import { usePlotGroupId } from '@/components/PlotGroups/PlotGroupsContextProvider';
import { useWorkstreamId } from '@/pages/shared/home/hooks/useWorkstreamId';
import { useNCAParentOrganizationId } from '@/pages/shared/hooks/useNCAParentOrganizationId';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetOrganizationLandTypeSuccessDto;
type ErrorDto = GetOrganizationLandTypeErrorDto;

export const useLandTypeDistribution = (organizationIdArg?: string, plotGroupIdArg?: string | null) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  const plotGroupId = usePlotGroupId();
  const ncaParentId = useNCAParentOrganizationId();
  const workstreamId = useWorkstreamId();

  assertIsDefined(organizationId, 'organizationId');

  const pathParams = { organization_id: organizationId };
  const queryParams = {
    plot_group_id: plotGroupIdArg ?? plotGroupId,
    ...(ncaParentId ? { nca_parent_id: ncaParentId } : {}),
    ...(workstreamId ? { workstream_id: workstreamId } : {}),
  };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_ORGANIZATION_LAND_TYPE_PATH, pathParams, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['organization', 'land-type', pathParams, queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
